import { useState } from 'react';
import { Button, DateInput } from '@safetyhub/ui';
import {
  organizationsApi,
  OrganizationCategory,
  CATEGORY_OPTIONS,
  extractApiError,
} from '../../lib/organizations';
import Modal from '../Modal';
import { useToastStore } from '../../stores/toast';
import { useCompanyDuplicateModal } from '../../hooks/useCompanyDuplicateModal';
import AddressSearch from '../AddressSearch';
import { validateBusinessNumberChecksum, normalizeRegistrationNumber } from '@safetyhub/shared';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

/**
 * 푸터의 제출 버튼이 본문의 폼을 가리키는 `form` 속성 값. base Modal이 본문과 푸터를 형제
 * div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 */
const FORM_ID = 'change-category-form';

interface ChangeCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: string;
  currentCategory: OrganizationCategory;
  hasCompany: boolean;
}

export default function ChangeCategoryModal({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  currentCategory,
  hasCompany,
}: ChangeCategoryModalProps) {
  const { showToast } = useToastStore();
  const { isEnabled } = useFeatureFlags();
  const enableGuidanceAgency = isEnabled('construction-guidance-agency');

  const visibleCategoryOptions = CATEGORY_OPTIONS.filter(
    (opt) => opt.value !== 'GUIDANCE_AGENCY' || enableGuidanceAgency,
  );

  const [selectedCategory, setSelectedCategory] = useState<OrganizationCategory>(currentCategory);
  const [isLoading, setIsLoading] = useState(false);

  // NTS 인증 정보 폼 상태 (Company 없는 경우)
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registrationNumberError, setRegistrationNumberError] = useState('');
  const [openingDate, setOpeningDate] = useState('');
  const [ceoName, setCeoName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [zonecode, setZonecode] = useState('');
  const [addressDetail, setAddressDetail] = useState('');

  // FULLY Company 중복 차단 모달 (ORGANIZATION_DUPLICATE_COMPANY)
  const { handleError: handleCompanyDuplicateError, modalNode: companyDuplicateModal } =
    useCompanyDuplicateModal();


  const isAgency =
    selectedCategory === 'SAFETY_MANAGEMENT_AGENCY' || selectedCategory === 'GUIDANCE_AGENCY';
  const isSameCategory = selectedCategory === currentCategory;
  const needsCompanyInfo = selectedCategory !== 'WORKSPACE' && !hasCompany;

  const handleRegistrationNumberChange = (value: string) => {
    const digits = normalizeRegistrationNumber(value).slice(0, 10);
    let formatted = digits;
    if (digits.length > 5) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    } else if (digits.length > 3) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    setRegistrationNumber(formatted);
    setRegistrationNumberError('');
  };

  const handleClose = () => {
    setSelectedCategory(currentCategory);
    setRegistrationNumber('');
    setRegistrationNumberError('');
    setOpeningDate('');
    setCeoName('');
    setCompanyName('');
    setAddress('');
    setZonecode('');
    setAddressDetail('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSameCategory) return;

    // Company 없는 경우 사업자 타입으로 변경 시 필수 필드 검증
    if (needsCompanyInfo) {
      if (!registrationNumber.trim()) {
        showToast('사업자등록번호를 입력해주세요.', 'error');
        return;
      }
      if (!validateBusinessNumberChecksum(registrationNumber)) {
        setRegistrationNumberError('유효하지 않은 사업자등록번호입니다.');
        showToast('유효하지 않은 사업자등록번호입니다.', 'error');
        return;
      }
      if (!openingDate.trim()) {
        showToast('개업일자를 입력해주세요.', 'error');
        return;
      }
      const dateDigits = openingDate.replace(/-/g, '');
      if (!/^\d{8}$/.test(dateDigits)) {
        showToast('유효한 개업일자를 입력해주세요.', 'error');
        return;
      }
      if (!ceoName.trim()) {
        showToast('대표자명을 입력해주세요.', 'error');
        return;
      }
    }

    setIsLoading(true);
    try {
      await organizationsApi.update(companyId, {
        category: selectedCategory,
        ...(needsCompanyInfo
          ? {
              company: {
                registrationNumber: registrationNumber.trim(),
                openingDate: openingDate.replace(/-/g, ''),
                ceoName: ceoName.trim(),
                name: companyName.trim() || ceoName.trim(),
                address: address.trim() || undefined,
                zonecode: zonecode.trim() || undefined,
                addressDetail: addressDetail.trim() || undefined,
              },
            }
          : {}),
      });
      showToast('조직 유형이 변경되었습니다.', 'success');
      onSuccess();
      handleClose();
    } catch (err) {
      if (!handleCompanyDuplicateError(err)) {
        const apiError = extractApiError(err);
        showToast(apiError.message, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderGuidanceText = () => {
    if (isSameCategory) return null;

    if (selectedCategory === 'WORKSPACE') {
      return (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-card">
          <p className="text-sm text-yellow-800">
            사업자 정보는 유지되며, 전용 보고서 생성이 제한됩니다.
          </p>
        </div>
      );
    }

    if (isAgency) {
      if (hasCompany) {
        return (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-card">
            <p className="text-sm text-orange-800 font-medium">
              카테고리 변경 후 기관 인증 서류 제출이 필요합니다.
            </p>
            <p className="text-sm text-orange-700 mt-1">
              관리자 심사 완료 전까지 기관 전용 기능이 제한됩니다.
            </p>
          </div>
        );
      } else {
        return (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-card">
            <p className="text-sm text-orange-800 font-medium">사업자 인증이 필요합니다.</p>
            <p className="text-sm text-orange-700 mt-1">
              사업자 인증 후 기관 인증 서류 제출 및 관리자 심사가 추가로 필요합니다.
            </p>
          </div>
        );
      }
    }

    // WORKPLACE
    if (hasCompany) {
      return (
        <div className="p-3 bg-green-50 border border-green-200 rounded-card">
          <p className="text-sm text-green-800">즉시 변경됩니다.</p>
        </div>
      );
    }

    return null;
  };

  const submitDisabled =
    isLoading ||
    isSameCategory ||
    (needsCompanyInfo && (!registrationNumber || !openingDate || !ceoName.trim()));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="조직 유형 변경"
      size="md"
      preventClose={isLoading}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            취소
          </Button>
          {/* 푸터는 base가 본문과 **형제**로 렌더하므로 아래 `<form>` 밖이다 — `form` 속성으로
              이어야 제출이 일어난다(없으면 조용히 아무 일도 하지 않는다). */}
          <Button variant="primary" type="submit" form={FORM_ID} disabled={submitDisabled}>
            {isLoading ? '변경 중...' : '변경'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* 카테고리 라디오 버튼 */}
          <div className="space-y-2">
            {visibleCategoryOptions.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center gap-3 p-3 rounded-card border cursor-pointer transition-colors ${
                  selectedCategory === opt.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-ink-200 bg-white hover:border-ink-300'
                }`}
              >
                <input
                  type="radio"
                  name="selectedCategory"
                  value={opt.value}
                  checked={selectedCategory === opt.value}
                  onChange={() => setSelectedCategory(opt.value)}
                  disabled={isLoading}
                  className="h-4 w-4 text-brand-600 border-ink-300 focus:ring-brand-500"
                />
                <span className="text-sm font-medium text-ink-800">
                  {opt.label}
                  {opt.value === currentCategory && (
                    <span className="ml-2 text-xs text-ink-400 font-normal">(현재)</span>
                  )}
                </span>
              </label>
            ))}
          </div>

          {/* 안내 텍스트 */}
          {renderGuidanceText()}

          {/* NTS 인증 정보 입력 폼 (Company 없는 경우) */}
          {!isSameCategory && needsCompanyInfo && (
            <div className="border border-blue-200 rounded-card p-4 bg-blue-50 space-y-3">
              <p className="text-xs text-blue-700 font-medium">사업자 정보</p>
              <p className="text-xs text-blue-600">
                국세청 사업자등록 정보와 일치해야 합니다. 사업자등록증을 참고해 정확히 입력해
                주세요.
              </p>

              <div>
                <label
                  htmlFor="change-reg-number"
                  className="block text-sm font-medium text-ink-700"
                >
                  사업자등록번호 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="change-reg-number"
                  value={registrationNumber}
                  onChange={(e) => handleRegistrationNumberChange(e.target.value)}
                  disabled={isLoading}
                  className={`mt-1 block w-full px-3 py-2 border rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white ${
                    registrationNumberError ? 'border-red-500' : 'border-ink-300'
                  }`}
                  placeholder="123-45-67890"
                />
                {registrationNumberError && (
                  <p className="mt-1 text-xs text-red-600">{registrationNumberError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700">
                  개업일자 <span className="text-red-500">*</span>
                </label>
                <div className="relative mt-1">
                  <DateInput
                    label="개업일자"
                    placeholder="개업일자를 선택하세요"
                    value={openingDate}
                    onChange={setOpeningDate}
                    disabled={isLoading}
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="change-ceo-name" className="block text-sm font-medium text-ink-700">
                  대표자명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="change-ceo-name"
                  value={ceoName}
                  onChange={(e) => setCeoName(e.target.value)}
                  disabled={isLoading}
                  className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                  placeholder="홍길동"
                />
              </div>

              <div>
                <label
                  htmlFor="change-company-name"
                  className="block text-sm font-medium text-ink-700"
                >
                  상호명
                </label>
                <input
                  type="text"
                  id="change-company-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={isLoading}
                  className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                  placeholder="(주)안전사업장"
                />
              </div>

              <div>
                <AddressSearch
                  id="change-company-address"
                  address={address}
                  addressDetail={addressDetail}
                  zonecode={zonecode}
                  onChange={(data) => {
                    setAddress(data.address);
                    setAddressDetail(data.addressDetail);
                    setZonecode(data.zonecode);
                  }}
                  label="사업장 주소"
                  disabled={isLoading}
                />
              </div>
            </div>
          )}
        </div>

      </form>

      {/* FULLY Company 중복 차단 모달 */}
      {companyDuplicateModal}
    </Modal>
  );
}
